import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Logs.css';

function Logs() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    limit: 100,
    offset: 0,
    type: '',
    user: '',
    startDate: '',
    endDate: ''
  });
  const [total, setTotal] = useState(0);
  const [openDetails, setOpenDetails] = useState({});

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/logs?${params.toString()}`);
      // Flatten the message property into the log object
      const flattenedLogs = response.data.logs.map(log => ({
        ...log.message,
        timestamp: log.timestamp,
        level: log.level
      }));
      setLogs(flattenedLogs);
      setTotal(response.data.total);
      setError(null);
    } catch (err) {
      setError('Failed to load logs');
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/logs/stats');
      setStats(response.data.stats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  const handlePageChange = (direction) => {
    setFilters(prev => ({
      ...prev,
      offset: direction === 'next'
        ? prev.offset + prev.limit
        : Math.max(0, prev.offset - prev.limit)
    }));
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const handleCopyToClipboard = (log) => {
    const text = JSON.stringify(log, null, 2);
    navigator.clipboard.writeText(text).then(() => {
      // Optional: Could add a toast notification here
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const handleDetailsToggle = (idx, isOpen) => {
    setOpenDetails(prev => ({
      ...prev,
      [idx]: isOpen
    }));
  };

  if (loading && logs.length === 0) {
    return (
      <div className="logs-container">
        <div className="loading">Loading logs...</div>
      </div>
    );
  }

  return (
    <div className="logs-container">
      <div className="logs-header">
        <h1>Framework Logs</h1>
        <button onClick={() => navigate('/')} className="back-button">
          Back to Builder
        </button>
      </div>

      {stats && (
        <div className="logs-stats">
          <div className="stat-card-single">
            <div className="stat-header">
              <h3>Total Operations</h3>
              <p className="stat-value">{stats.totalOperations}</p>
            </div>

            {stats.byUser && Object.keys(stats.byUser).length > 0 && (
              <div className="user-stats-grid">
                {Object.entries(stats.byUser).map(([user, count]) => (
                  <div key={user} className="user-stat-item">
                    {user}: <span className="user-count">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <div className="logs-table-container">
        <table className="logs-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>User</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log, idx) => (
              <tr key={idx}>
                <td>{formatTimestamp(log.timestamp)}</td>
                <td>{log.user ? log.user.name : 'N/A'}</td>
                <td>
                  <details onToggle={(e) => handleDetailsToggle(idx, e.target.open)}>
                    <summary>
                      View Details
                      {openDetails[idx] && (
                        <button
                          className="copy-button"
                          onClick={(e) => {
                            e.preventDefault();
                            handleCopyToClipboard(log);
                          }}
                          title="Copy to clipboard"
                        >
                          Copy
                        </button>
                      )}
                    </summary>
                    <pre className="log-details">
                      {JSON.stringify(log, null, 2)}
                    </pre>
                  </details>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button
          onClick={() => handlePageChange('prev')}
          disabled={filters.offset === 0}
          className="pagination-button"
        >
          Previous
        </button>
        <span className="pagination-info">
          Showing {filters.offset + 1}-{Math.min(filters.offset + filters.limit, total)} of {total}
        </span>
        <button
          onClick={() => handlePageChange('next')}
          disabled={filters.offset + filters.limit >= total}
          className="pagination-button"
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default Logs;
